import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Panel from "@ui/panel/Panel";
import { GD } from "src/GD";
import { Sessions } from "../forms/Sessions";
import { getChartTransactionsFilters, type ChartBucket, type ChartTransactionScope } from "./chartTransactions";

interface Props { scope: ChartTransactionScope; bucket: ChartBucket | null; onClose: () => void }
const formatTime = (value: string) => value.slice(0, 16).replace("T", " ");

export const ChartTransactionsPopup = ({ scope, bucket, onClose }: Props) => {
	 useLocale();
	const popup = useRef<HTMLDivElement>(null);
	const [context, setContext] = useState<{ scope: ChartTransactionScope; bucket: ChartBucket | null; signal: AbortSignal; error?: string }>();
	const fixedFilters = useMemo(() => bucket ? getChartTransactionsFilters(scope, bucket) : undefined, [scope, bucket]);
	const ready = context?.scope === scope && context.bucket === bucket && !context.signal.aborted;
	const requestOptions = useMemo(() => ({ expectedProjectId: scope.projectId, signal: context?.signal }), [scope.projectId, context?.signal]);

	useEffect(() => {
		const previousFocus = document.activeElement;
		popup.current?.focus();
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(onClose);
		return () => {
			GD.S_AUTH_LOG_OUT.unsubscribe(subscription);
			if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
		};
	}, [onClose]);

	useEffect(() => {
		const abort = new AbortController();
		const checkContext = async () => {
			try {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				abort.signal.throwIfAborted();
				if (profile?.id !== scope.userId || project?.id !== scope.projectId) throw new Error(msg("contextChangedReopenTheChart"));
				setContext({ scope, bucket, signal: abort.signal });
			} catch (cause) {
				if (!abort.signal.aborted) setContext({ scope, bucket, signal: abort.signal, error: cause instanceof Error ? cause.message : msg("couldNotLoadTransactionsReopenTheChart") });
			}
		};
		void checkContext();
		return () => abort.abort();
	}, [scope, bucket]);

	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={msg("transactionsForChartInterval")} tabIndex={-1}
		onKeyDown={event => {
			if (event.key !== "Tab") return;
			const controls = popup.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex="0"]');
			const first = controls?.[0];
			const last = controls?.[controls.length - 1];
			if (!first || !last) { event.preventDefault(); return; }
			if (event.shiftKey && (document.activeElement === first || document.activeElement === popup.current)) { event.preventDefault(); last.focus(); }
			else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
		}}>
		<Panel decorator="absolute" label={msg("transactions")} onClose={onClose} growContent disableScroll
			sx={{ width: "1400px", maxWidth: "94vw", height: "80dvh", maxHeight: "90dvh", overflow: "hidden" }}>
			<div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minHeight: 0 }}>
				<div style={{ fontSize: 13, color: "#667085", flexShrink: 0 }}>
					{bucket && <div>{formatTime(bucket.from)} — {formatTime(bucket.to)} UTC</div>}
					<div>{scope.currency}{msg("allChannelsAllStatuses")}</div>
				</div>
				{!bucket ? <div role="alert">{msg("couldNotDetermineThisPointSTimeInterval")}</div>
					: !ready ? <div role="status">{msg("loadingTransactions_d69971d3")}</div>
					: context.error ? <div role="alert">{context.error}</div>
					: <Sessions key={JSON.stringify([scope, bucket])} target_id={scope.target_id} target_type={scope.target_type}
						filterable={false} tableScrollable showSummary={false} fixedFilters={fixedFilters} requestOptions={requestOptions} />}
			</div>
		</Panel>
	</div>, document.body);
};
